<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Policy from 'dashboard/components/policy.vue';
import MeetingInvitesAPI from 'dashboard/api/meetingInvites';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const [showActionsDropdown, toggleDropdown] = useToggle(false);
const showMeetingModal = ref(false);
const isDownloadingTranscript = ref(false);
const meetingInviteLink = ref('');
const agentMeetingInviteLink = ref('');
const isCreatingMeeting = ref(false);
const isSendingMeetingLink = ref(false);
const isOpeningMeetingLink = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);
const isChatCallEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    FEATURE_FLAGS.CHAT_CALLS
  )
);
const meetingInviteLabel = 'Chamada com Cliente';
const sendMeetingInviteLabel = 'Enviar';
const openMeetingInviteLabel = 'Ir para chamada';
const copyAgentMeetingInviteLabel = 'Copiar link do agente';

const actionMenuItems = computed(() => []);

const toggleMuteFromToolbar = () => {
  if (!currentChat.value?.id) return;
  if (currentChat.value.muted) {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  }
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

const downloadConversationTranscript = async () => {
  if (!currentChat.value?.id || isDownloadingTranscript.value) return;
  isDownloadingTranscript.value = true;
  try {
    await store.dispatch('downloadTranscript', {
      conversationId: currentChat.value.id,
    });
    useAlert(t('EMAIL_TRANSCRIPT.DOWNLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('EMAIL_TRANSCRIPT.DOWNLOAD_ERROR'));
  } finally {
    isDownloadingTranscript.value = false;
  }
};

if (isAdmin.value) {
  emitter.on(CMD_MUTE_CONVERSATION, mute);
  emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
}
emitter.on(CMD_SEND_TRANSCRIPT, downloadConversationTranscript);

const copyMeetingInvite = async () => {
  if (!meetingInviteLink.value) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(meetingInviteLink.value);
      useAlert('Link copiado');
    } else {
      useAlert(meetingInviteLink.value);
    }
  } catch (error) {
    useAlert('Não foi possível copiar o link');
  }
};

const copyAgentMeetingInvite = async () => {
  if (!agentMeetingInviteLink.value) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(agentMeetingInviteLink.value);
      useAlert('Link do agente copiado');
    } else {
      useAlert(agentMeetingInviteLink.value);
    }
  } catch (error) {
    useAlert('Não foi possível copiar o link do agente');
  }
};

const sendMeetingInviteLink = async () => {
  if (!meetingInviteLink.value || !currentChat.value?.id) return;
  isSendingMeetingLink.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      message: meetingInviteLink.value,
      conversationId: currentChat.value.id,
    });
    useAlert('Link enviado para o cliente');
    showMeetingModal.value = false;
  } catch (error) {
    useAlert('Não foi possível enviar o link');
  } finally {
    isSendingMeetingLink.value = false;
  }
};

const goToMeetingInvite = () => {
  const targetUrl = agentMeetingInviteLink.value || meetingInviteLink.value;
  if (!targetUrl || isOpeningMeetingLink.value) return;

  isOpeningMeetingLink.value = true;
  window.open(targetUrl, '_blank', 'noopener,noreferrer');

  window.setTimeout(() => {
    isOpeningMeetingLink.value = false;
  }, 300);
};

const closeMeetingModal = () => {
  showMeetingModal.value = false;
};

const createMeetingInvite = async () => {
  if (!currentChat.value?.id || !isChatCallEnabled.value) return;
  isCreatingMeeting.value = true;
  try {
    const response = await MeetingInvitesAPI.create(
      accountId.value,
      currentChat.value.id,
      currentUser.value?.access_token
    );
    meetingInviteLink.value = response.data?.meeting_invite?.join_url || '';
    agentMeetingInviteLink.value =
      response.data?.meeting_invite?.agent_join_url || meetingInviteLink.value;
    showMeetingModal.value = !!meetingInviteLink.value;
    if (!meetingInviteLink.value) {
      useAlert('Não foi possível gerar o link da chamada');
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível criar a chamada'
    );
  } finally {
    isCreatingMeeting.value = false;
  }
};

onUnmounted(() => {
  if (isAdmin.value) {
    emitter.off(CMD_MUTE_CONVERSATION, mute);
    emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  }
  emitter.off(CMD_SEND_TRANSCRIPT, downloadConversationTranscript);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <Policy :permissions="['administrator']">
      <ButtonV4
        v-tooltip="
          currentChat?.muted
            ? t('CONTACT_PANEL.UNMUTE_CONTACT')
            : t('CONTACT_PANEL.MUTE_CONTACT')
        "
        :aria-label="
          currentChat?.muted
            ? t('CONTACT_PANEL.UNMUTE_CONTACT')
            : t('CONTACT_PANEL.MUTE_CONTACT')
        "
        :title="
          currentChat?.muted
            ? t('CONTACT_PANEL.UNMUTE_CONTACT')
            : t('CONTACT_PANEL.MUTE_CONTACT')
        "
        size="sm"
        variant="solid"
        color="slate"
        :icon="currentChat?.muted ? 'i-lucide-volume-1' : 'i-lucide-ban'"
        :disabled="!currentChat?.id"
        class="flex-shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
        @click="toggleMuteFromToolbar"
      />
    </Policy>
    <ButtonV4
      v-if="isChatCallEnabled"
      v-tooltip="meetingInviteLabel"
      :aria-label="meetingInviteLabel"
      :title="meetingInviteLabel"
      size="sm"
      variant="solid"
      color="slate"
      icon="i-lucide-video"
      :is-loading="isCreatingMeeting"
      :disabled="!currentChat?.id || isCreatingMeeting"
      class="flex-shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
      @click="createMeetingInvite"
    />
    <ButtonV4
      v-tooltip="t('CONTACT_PANEL.SEND_TRANSCRIPT')"
      :aria-label="t('CONTACT_PANEL.SEND_TRANSCRIPT')"
      :title="t('CONTACT_PANEL.SEND_TRANSCRIPT')"
      size="sm"
      variant="solid"
      color="slate"
      icon="i-lucide-download"
      :is-loading="isDownloadingTranscript"
      :disabled="!currentChat?.id || isDownloadingTranscript"
      class="flex-shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
      @click="downloadConversationTranscript"
    />
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-if="actionMenuItems.length > 0"
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <woot-modal
      v-model:show="showMeetingModal"
      :on-close="closeMeetingModal"
      size="small"
    >
      <woot-modal-header header-title="Meet iuConnect" />
      <div class="p-4 space-y-3">
        <div class="space-y-1">
          <div class="text-xs font-medium text-n-slate-11">Link do cliente</div>
          <div class="flex items-center gap-2">
            <input class="w-full input" readonly :value="meetingInviteLink" />
            <woot-button
              type="button"
              variant="clear"
              size="small"
              icon="copy"
              :disabled="!meetingInviteLink"
              @click="copyMeetingInvite"
            />
          </div>
        </div>
        <div class="space-y-1">
          <div class="text-xs font-medium text-n-slate-11">Link do agente</div>
          <div class="flex items-center gap-2">
            <input class="w-full input" readonly :value="agentMeetingInviteLink" />
            <woot-button
              type="button"
              variant="clear"
              size="small"
              icon="copy"
              :disabled="!agentMeetingInviteLink"
              @click="copyAgentMeetingInvite"
            />
          </div>
          <p class="text-xs text-n-slate-11">
            Use este link para abrir a reunião com permissão de gravação.
          </p>
        </div>
        <div class="flex items-center gap-2 justify-end">
          <woot-button
            type="button"
            variant="clear"
            color-scheme="secondary"
            :disabled="!agentMeetingInviteLink"
            @click="copyAgentMeetingInvite"
          >
            {{ copyAgentMeetingInviteLabel }}
          </woot-button>
          <woot-button
            type="button"
            variant="clear"
            color-scheme="secondary"
            :loading="isSendingMeetingLink"
            :disabled="!meetingInviteLink"
            @click="sendMeetingInviteLink"
          >
            {{ sendMeetingInviteLabel }}
          </woot-button>
          <woot-button
            type="button"
            color-scheme="primary"
            variant="solid"
            :disabled="!meetingInviteLink || isOpeningMeetingLink"
            @click="goToMeetingInvite"
          >
            {{ openMeetingInviteLabel }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
